// SPDX-License-Identifier: LicenseRef-Proprietary

import { useState, useEffect, useRef } from 'react';
import { pubmedDetails, type PubmedPaperDetails } from '../../../../../services/pubmedService';

export interface UsePaperDetailsResult {
  selectedPmid: string | null;
  details: PubmedPaperDetails | null;
  isLoadingDetails: boolean;
  error: string | null;
  loadDetails: (pmid: string | null) => void;
}

export interface UsePaperDetailsOptions {
  getCachedDetails: (pmid: string) => PubmedPaperDetails | undefined;
  cacheDetails: (pmid: string, details: PubmedPaperDetails) => void;
}

export function usePaperDetails(options: UsePaperDetailsOptions): UsePaperDetailsResult {
  const { getCachedDetails, cacheDetails } = options;
  const [selectedPmid, setSelectedPmid] = useState<string | null>(null);
  const [details, setDetails] = useState<PubmedPaperDetails | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!selectedPmid) {
      setDetails(null);
      setError(null);
      return;
    }

    // Check cache first
    const cached = getCachedDetails(selectedPmid);
    if (cached) {
      setDetails(cached);
      setIsLoadingDetails(false);
      setError(null);
      return;
    }

    // Fetch details
    setIsLoadingDetails(true);
    setError(null);
    const controller = new AbortController();
    abortControllerRef.current = controller;

    pubmedDetails({ pmid: selectedPmid }, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }
        try {
          setDetails(response.data);
          cacheDetails(selectedPmid, response.data);
          setIsLoadingDetails(false);
        } catch (err) {
          // Handle errors during state updates
          if (!controller.signal.aborted) {
            setIsLoadingDetails(false);
            setError(err instanceof Error ? err.message : 'Failed to process details');
          }
        }
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        // Only update state if request wasn't aborted
        if (!controller.signal.aborted) {
          setIsLoadingDetails(false);
          setError(err instanceof Error ? err.message : 'Failed to load details');
        }
      });

    return () => {
      controller.abort();
      abortControllerRef.current = null;
    };
  }, [selectedPmid, getCachedDetails, cacheDetails]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    selectedPmid,
    details,
    isLoadingDetails,
    error,
    loadDetails: setSelectedPmid,
  };
}

